/* eslint-disable react/jsx-props-no-spreading */
import {
  Box,
  Checkbox,
  Collapse,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Grid,
  Radio,
  RadioGroup,
  Typography,
  TextField,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { Stack } from '@mui/system';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { NumericFormat, PatternFormat } from 'react-number-format';

import { Button, InfoTooltip } from '../../../components/core';
import {
  CustomerFieldType,
  DeliveryMethod,
  FormValues,
  getFieldTypeFromInput,
  getMissingCustomerFields,
  sanitizeNumericString,
  getValidationSchema,
  getPaylinkDescriptionHelperText,
  PAYLINK_DESCRIPTION_MAX_LENGTH,
} from './formValues';
import {
  CreatePaylinkBody,
  PaylinkCustomerInfoResponse,
} from '../../../store/modules/paylink/types';
import { isPaylinkCustomerInfo } from '../../../util/helpers';
import { DatePicker } from '../components/DatePicker';
import { CustomerAutoComplete, getOptionLabel } from '../../../common/CustomerAutoComplete';
import AttachmentButton from '../components/AttachmentButton';
import { AcceptedCurrency } from '../../../store/modules/merchant/types';
import { usePaylinkDescriptionPasteFeedback } from '../hooks';

const formDefaultValues: Partial<FormValues> = {
  amount: '',
  expiration: null,
  customerPhone: '',
  customer: '',
  deliveryMethod: null,
};

interface SendPaylinkFormProps {
  merchantId: string;
  merchantName: string;
  isLoadingSubmit?: boolean;
  resetFormOnSubmit?: boolean;
  serverValidationError?: string | null;
  onSubmit: (
    data: CreatePaylinkBody,
    saveCustomerInformation: boolean,
    attachment: File | null,
    isSavingCustomerRequired: boolean,
  ) => Promise<void>;
  isModal?: boolean;
  currency?: AcceptedCurrency;
  isRecurringCheckboxEnabled?: boolean;
  isPartialPaymentCheckboxEnabled?: boolean;
  preSelectedCustomer?: PaylinkCustomerInfoResponse;
}

const SendPaylinkForm = ({
  merchantId,
  merchantName,
  onSubmit,
  isLoadingSubmit,
  serverValidationError,
  resetFormOnSubmit = true,
  isModal = false,
  currency,
  isRecurringCheckboxEnabled = false,
  isPartialPaymentCheckboxEnabled = false,
  preSelectedCustomer,
}: SendPaylinkFormProps): JSX.Element => {
  const [isSaveCustomerChecked, setIsSaveCustomerChecked] = useState(false);

  // If the recurring checkbox is enabled, it is required to save customer information
  const shouldSaveCustomer = isSaveCustomerChecked || isRecurringCheckboxEnabled;

  const [isRecurringEnabled, setIsRecurringEnabled] = useState(false);
  const [isPartialPaymentEnabled, setIsPartialPaymentEnabled] = useState(false);
  const [hasCustomerConstent, setHasCustomerConstent] = useState(false);
  const [hasRecurringChargeConsent, setHasRecurringChargeConsent] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState<PaylinkCustomerInfoResponse | null>(
    preSelectedCustomer ?? null,
  );
  const [attachment, setAttachment] = useState<File | null>(null);
  const currencySymbol = currency?.symbol || '$';
  const validationSchema = getValidationSchema(selectedCustomer);
  const {
    register,
    control,
    handleSubmit,
    formState: { errors, isSubmitSuccessful },
    watch,
    reset,
    setValue,
  } = useForm<FormValues>({
    resolver: zodResolver(validationSchema),
    defaultValues: {
      ...formDefaultValues,
      ...(preSelectedCustomer && {
        customer: getOptionLabel(preSelectedCustomer),
        customerPhone: preSelectedCustomer.phone ?? undefined,
        customerEmail: preSelectedCustomer.email ?? undefined,
        firstName: preSelectedCustomer.firstName ?? undefined,
        lastName: preSelectedCustomer.lastName ?? undefined,
      }),
    },
    reValidateMode: 'onBlur',
  });

  const descriptionValue = watch('description');
  const { pasteTrimmedMessage, onDescriptionPaste } = usePaylinkDescriptionPasteFeedback();

  const onFormSubmit = async (data: FormValues) => {
    const {
      customer,
      customerPhone,
      deliveryMethod,
      firstName,
      lastName,
      customerEmail,
      expiration,
      description,
      merchantInvoiceId,
    } = data;
    const sendEmail =
      deliveryMethod === DeliveryMethod.EMAIL || deliveryMethod === DeliveryMethod.TEXT_AND_EMAIL;
    const sendSms =
      deliveryMethod === DeliveryMethod.TEXT || deliveryMethod === DeliveryMethod.TEXT_AND_EMAIL;
    const body: CreatePaylinkBody = {
      lineItems: [{ sku: 'Paylink Amount', price: Number(sanitizeNumericString(data.amount)) }],
      merchantId,
      customerConsent: hasCustomerConstent,
      sendEmail,
      sendSms,
      ...(merchantInvoiceId?.length && { merchantInvoiceId }),
      ...(description?.length && { description }),
      ...(expiration && { expiration: expiration.toISOString() }),
      customer: {
        ...(customerEmail?.length && { email: customerEmail }),
        ...(customerPhone?.length && { phone: sanitizeNumericString(customerPhone) }),
        ...(firstName?.length && { firstName }),
        ...(lastName?.length && { lastName }),
      },
    };

    if (deliveryMethod === DeliveryMethod.TEXT) {
      delete body.customer.email;
    } else if (deliveryMethod === DeliveryMethod.EMAIL) {
      delete body.customer.phone;
    }

    if (selectedCustomer) {
      body.customer = { customerId: selectedCustomer.payLinkCustomerId };

      // Provide email and phone if they are missing from the selected customer
      // We will update the customer record with this information
      if (!selectedCustomer.email && sendEmail) body.customer.email = customerEmail;
      if (!selectedCustomer.phone && sendSms)
        body.customer.phone = sanitizeNumericString(customerPhone);
    } else {
      const customerInputType = getFieldTypeFromInput(customer);
      if (customerInputType === CustomerFieldType.PHONE) {
        body.customer.phone = sanitizeNumericString(customer);
      } else {
        body.customer.email = customer;
      }
    }

    if (isRecurringEnabled) {
      body.enableRecurring = true;
      body.recurringChargeConsent = hasRecurringChargeConsent;
    }

    if (isPartialPaymentEnabled) {
      body.enablePartialPayment = true;
    }

    await onSubmit(body, shouldSaveCustomer, attachment, isRecurringCheckboxEnabled);
  };

  useEffect(() => {
    if (!isSubmitSuccessful || !resetFormOnSubmit) {
      return;
    }

    setSelectedCustomer(null);
    const lastDeliveryMethod = watch('deliveryMethod');
    reset(undefined);
    setValue('deliveryMethod', lastDeliveryMethod);
    setHasCustomerConstent(false);
    setAttachment(null);
    setIsRecurringEnabled(false);
    setIsPartialPaymentEnabled(false);
    setHasRecurringChargeConsent(false);
  }, [isSubmitSuccessful, reset, resetFormOnSubmit, setValue, watch]);

  const deliveryMethod = watch('deliveryMethod');
  const missingCustomerFields = getMissingCustomerFields(
    watch('customer'),
    deliveryMethod,
    selectedCustomer,
  );

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: 'white',
        borderRadius: 3,
        pt: isModal ? 3 : { xs: 2, md: 4 },
        pb: isModal ? 0 : 1.5,
        px: isModal ? 3 : { xs: 2, md: 4 },
        filter: 'drop-shadow(0px 24px 48px rgba(16, 24, 40, 0.18))',
        '& .MuiFormControlLabel-label': {
          fontSize: '14px',
          color: 'grey.800',
        },
      }}
    >
      {!isModal && (
        <Typography
          component="h2"
          fontWeight="semibold"
          sx={{ fontSize: { xs: 14, sm: 16, md: 20 }, mb: 2 }}
        >
          Send a new Paylink
        </Typography>
      )}
      <form onSubmit={handleSubmit(onFormSubmit)} id="send-paylink">
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            paddingTop: 2,
            ...(isModal && {
              pt: 0,
              pb: 2,
              mt: 0,
              mx: -4,
              paddingLeft: 4,
              paddingRight: 4,
            }),
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 3,
              minHeight: isModal ? '450px' : '500px',
              marginBottom: { xs: 0, sm: 2, md: 4 },
            }}
          >
            <Controller
              name="customer"
              control={control}
              render={({ field: { onChange: fieldOnChange, value: fieldValue, ...rest } }) => (
                <CustomerAutoComplete
                  freeSolo
                  id="customer"
                  inputProps={{
                    label: 'Customer phone or email',
                    required: true,
                    error: !!errors.customer || !!serverValidationError,
                    helperText: errors.customer?.message ?? serverValidationError,
                    onChange: (e) => {
                      fieldOnChange(e);
                      if (selectedCustomer) {
                        setSelectedCustomer(null);
                      }
                    },
                    value: fieldValue,
                  }}
                  // MUI workaround: to clear autocomplete state in freesolo mode, value must use empty string instead of null to reset.
                  // see: https://github.com/mui/material-ui/issues/32537#issuecomment-1152022151
                  value={fieldValue === '' ? '' : selectedCustomer}
                  onChange={(_e, newSelectedCustomer) => {
                    if (!newSelectedCustomer || !isPaylinkCustomerInfo(newSelectedCustomer)) {
                      fieldOnChange(null);
                      setSelectedCustomer(null);
                      return;
                    }
                    setSelectedCustomer(newSelectedCustomer);
                    fieldOnChange(getOptionLabel(newSelectedCustomer));
                  }}
                  autoSelectCustomer
                  {...rest}
                />
              )}
            />
            <TextField
              variant="outlined"
              label="Description"
              {...register('description')}
              id="description"
              multiline
              minRows={2}
              inputProps={{
                maxLength: PAYLINK_DESCRIPTION_MAX_LENGTH,
                onPaste: onDescriptionPaste,
              }}
              error={!!errors.description}
              helperText={getPaylinkDescriptionHelperText(
                descriptionValue,
                errors.description?.message,
                pasteTrimmedMessage,
              )}
              FormHelperTextProps={
                pasteTrimmedMessage && !errors.description
                  ? { sx: { color: 'text.secondary' } }
                  : undefined
              }
            />
            <Grid container spacing={3} sx={{ '& > *': { pr: '0 !important' } }}>
              <Grid item xs={6} md={7}>
                <Controller
                  name="amount"
                  control={control}
                  render={({ field }) => (
                    <NumericFormat
                      customInput={TextField}
                      variant="outlined"
                      label="Amount"
                      required
                      thousandSeparator
                      prefix={currencySymbol}
                      fixedDecimalScale
                      decimalScale={2}
                      allowNegative={false}
                      error={!!errors.amount}
                      helperText={errors.amount?.message}
                      onChange={field.onChange}
                      value={field.value}
                      name={field.name}
                      id={field.name}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={6} md={5} sx={{ pl: { xs: '8px !important', sm: '16px !important' } }}>
                <Controller
                  name="expiration"
                  control={control}
                  render={({ field }) => (
                    <DatePicker
                      label="Expiration"
                      errorMessage={errors.expiration?.message}
                      {...field}
                    />
                  )}
                />
              </Grid>
            </Grid>
            <TextField
              variant="outlined"
              label="Merchant Reference"
              {...register('merchantInvoiceId')}
              id="merchantInvoiceId"
            />
            <Box>
              <FormControl sx={{ mb: 1 }}>
                <FormLabel
                  id="delivery-method-label"
                  sx={{
                    fontSize: '12px',
                    fontWeight: 400,
                    color: 'grey.800',
                  }}
                  required
                >
                  Delivery method
                </FormLabel>
                <Controller
                  name="deliveryMethod"
                  control={control}
                  render={({ field }) => (
                    <FormControl error={!!errors.deliveryMethod} sx={{ mb: 1 }}>
                      <RadioGroup
                        row
                        aria-labelledby="delivery-method-label"
                        {...field}
                        sx={{
                          '& .MuiRadio-root, .MuiRadio-root.Mui-checked': {
                            color: 'grey.800',
                            pr: { xs: 0.5, sm: 1 },
                          },
                          '& > *': {
                            mr: { xs: 1.5, sm: 2 },
                          },
                        }}
                      >
                        <FormControlLabel
                          value={DeliveryMethod.TEXT}
                          control={<Radio />}
                          label="Text"
                        />
                        <FormControlLabel
                          value={DeliveryMethod.EMAIL}
                          control={<Radio />}
                          label="Email"
                        />
                        <FormControlLabel
                          value={DeliveryMethod.TEXT_AND_EMAIL}
                          control={<Radio />}
                          label="Text and email"
                        />
                      </RadioGroup>
                      <FormHelperText
                        sx={{
                          fontWeight: 700,
                          lineHeight: '24px',
                          mt: -0.5,
                          ml: 0,
                        }}
                      >
                        {errors.deliveryMethod?.message}
                      </FormHelperText>
                    </FormControl>
                  )}
                />
              </FormControl>

              <Collapse in={!!deliveryMethod && missingCustomerFields.size > 0}>
                <Stack spacing={2} sx={{ mb: 2 }}>
                  {missingCustomerFields.has(CustomerFieldType.PHONE) && (
                    <Controller
                      name={CustomerFieldType.PHONE}
                      control={control}
                      render={({ field }) => (
                        <PatternFormat
                          customInput={TextField}
                          variant="outlined"
                          label="Phone"
                          format="(###) ###-####"
                          required={shouldSaveCustomer}
                          error={!!errors.customerPhone}
                          helperText={errors.customerPhone?.message}
                          onChange={field.onChange}
                          value={field.value}
                          name={field.name}
                          id={field.name}
                        />
                      )}
                    />
                  )}
                  {missingCustomerFields.has(CustomerFieldType.EMAIL) && (
                    <TextField
                      variant="outlined"
                      label="Email"
                      type="email"
                      required={shouldSaveCustomer}
                      error={!!errors[CustomerFieldType.EMAIL]}
                      helperText={errors[CustomerFieldType.EMAIL]?.message}
                      id={CustomerFieldType.EMAIL}
                      {...register(CustomerFieldType.EMAIL)}
                    />
                  )}
                </Stack>
              </Collapse>

              {!isRecurringCheckboxEnabled && (
                <Collapse in={!selectedCustomer}>
                  <FormControlLabel
                    sx={{ mb: 1 }}
                    control={
                      <Checkbox
                        onChange={() => setIsSaveCustomerChecked((prev) => !prev)}
                        checked={isSaveCustomerChecked}
                        inputProps={{ 'aria-labelledby': 'save-customer-information' }}
                      />
                    }
                    label="Save customer information"
                    id="save-customer-information"
                  />
                </Collapse>
              )}

              <Collapse in={shouldSaveCustomer && !selectedCustomer}>
                <Stack
                  sx={{
                    flexDirection: { xs: 'column', sm: 'row' },
                    gap: 2,
                    mb: 1,
                  }}
                >
                  <TextField
                    variant="outlined"
                    label="Customer first name"
                    error={!!errors.firstName}
                    helperText={errors.firstName?.message}
                    required={shouldSaveCustomer && !selectedCustomer}
                    id="firstName"
                    {...register('firstName')}
                  />
                  <TextField
                    variant="outlined"
                    label="Customer last name"
                    error={!!errors.lastName}
                    helperText={errors.lastName?.message}
                    required={shouldSaveCustomer && !selectedCustomer}
                    id="lastName"
                    {...register('lastName')}
                  />
                </Stack>
              </Collapse>
              {isRecurringCheckboxEnabled && (
                <Stack direction="row" alignItems="center" spacing={0.5} sx={{ ml: '-11px' }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        onChange={() => setIsRecurringEnabled((prev) => !prev)}
                        checked={isRecurringEnabled}
                        inputProps={{ 'aria-labelledby': 'recurring-checkbox' }}
                      />
                    }
                    label="Save card on file"
                    id="recurring-checkbox"
                  />
                  <InfoTooltip
                    title="Card will only be saved for US-based shoppers"
                    fontSize={16}
                    ariaLabel="Save card on file details"
                  />
                </Stack>
              )}
              {isPartialPaymentCheckboxEnabled && (
                <Stack direction="row" alignItems="center" spacing={0.5} sx={{ ml: '-11px' }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        onChange={() => setIsPartialPaymentEnabled((prev) => !prev)}
                        checked={isPartialPaymentEnabled}
                        inputProps={{ 'aria-labelledby': 'partial-payment-checkbox' }}
                      />
                    }
                    label="Allow partial payment"
                    id="partial-payment-checkbox"
                  />
                  <InfoTooltip
                    title="Shoppers can choose how much to pay on this paylink, up to the paylink total"
                    fontSize={16}
                    ariaLabel="Allow partial payment details"
                  />
                </Stack>
              )}
            </Box>
          </Box>
          <Grid container>
            <AttachmentButton attachment={attachment} setAttachment={setAttachment} />
          </Grid>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
              '& .MuiFormControlLabel-label': {
                fontSize: { xs: '11px !important', sm: '12px !important', md: '14px !important' },
              },
            }}
          >
            <FormControlLabel
              control={
                <Checkbox
                  onChange={() => setHasCustomerConstent((prev) => !prev)}
                  checked={hasCustomerConstent}
                  required
                  inputProps={{
                    'aria-labelledby': 'customer-consent',
                    'aria-required': 'true',
                  }}
                />
              }
              label="By continuing, you are confirming you have collected the customer's consent to receive communication"
              id="customer-consent"
            />
            {isRecurringCheckboxEnabled && (
              <Collapse in={isRecurringCheckboxEnabled && isRecurringEnabled}>
                <FormControlLabel
                  control={
                    <Checkbox
                      onChange={() => setHasRecurringChargeConsent((prev) => !prev)}
                      checked={hasRecurringChargeConsent}
                      required={isRecurringEnabled}
                      inputProps={{
                        'aria-labelledby': 'recurring-charge-consent',
                        'aria-required': isRecurringEnabled ? 'true' : 'false',
                      }}
                    />
                  }
                  label="Customer has given written permission to charge their card for future purchases"
                  id="recurring-charge-consent"
                />
              </Collapse>
            )}
            <Button
              text="Send Paylink"
              isFullWidth
              type="submit"
              form="send-paylink"
              disabled={
                !hasCustomerConstent ||
                isLoadingSubmit ||
                (isRecurringEnabled && !hasRecurringChargeConsent)
              }
              loading={isLoadingSubmit}
            />
            <Typography
              sx={{
                fontSize: '14px',
                color: 'grey.800',
                textAlign: 'center',
              }}
            >
              Sending from {merchantName}
            </Typography>
          </Box>
        </Box>
      </form>
    </Box>
  );
};

export default SendPaylinkForm;
